import { registerImageScaler } from "@crown/ui/Image/image-scalers";
import type { ImageScaler } from "@crown/ui/Image/sizing";
import md5 from "md5";
import {
  getBunnyCdnCentraBaseUrl,
  getBunnyCdnProductsBaseUrl,
  getBunnyCdnUserFilesBaseUrl,
  getImgIxBaseUrl,
  getImgIxToken,
  getStoryblokImageUrl,
  getUseBunnyCdnCentra,
  getUseBunnyCdnUserFiles,
} from "./utils/env/env";
export { getSizing } from "@crown/ui/Image/sizing";

const imgIxBaseUrl = getImgIxBaseUrl();
const imgIxToken = getImgIxToken();

const expandStoryblokImageUrl = (url: string) => {
  if (url.startsWith("/")) {
    return getStoryblokImageUrl() + url;
  } else {
    return url;
  }
};

const productImageAspect = 4 / 3;

const LEGACY_USERFILES_HOST = "xwuf.imgix.net";

const imgIxImageScalerLegacy: ImageScaler = ({
  src,
  width,
  height,
  format,
  rawOpts,
}) => {
  if (src.includes("imgix") || src.startsWith("data:")) {
    const isFitInRawOpts = rawOpts && rawOpts?.indexOf("fit=") > -1;

    return src.startsWith("data:")
      ? src
      : `${src.replace(/ /g, "%20")}?auto=compress%2Cformat&w=${width}${isFitInRawOpts ? "" : "&fit=clamp"
      }${height ? `&h=${height}` : ""}&q=${format == "lowres" ? 5 : 50}${rawOpts ? "&" + rawOpts : ""
      }`;
  } else {
    return src;
  }
};

export const userFilesBunnyImageScaler: ImageScaler = ({
  src,
  width,
  height,
  format,
  rawOpts,
}) => {
  if (src.startsWith("data:")) {
    return src;
  }

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  const baseUrl = getBunnyCdnUserFilesBaseUrl().replace(/\/$/, "");
  let bunnyHost: string;
  try {
    bunnyHost = new URL(baseUrl).host;
  } catch {
    return src;
  }

  // Match legacy imgix-hosted uploads (rewrite to Bunny) and URLs that are
  // already on the Bunny pull zone (add Bunny Optimizer query params).
  if (url.host !== LEGACY_USERFILES_HOST && url.host !== bunnyHost) {
    return src;
  }

  const query =
    `?width=${width}` +
    (height ? `&height=${height}` : "") +
    `&format=auto` +
    `&quality=${format == "lowres" ? 5 : 50}` +
    (rawOpts ? `&${rawOpts}` : "");

  return `${baseUrl}${url.pathname}${query}`;
};

// Public scaler that direct callers (and the registry) import. Dispatches
// to the Bunny implementation when the feature flag is on, otherwise the
// legacy imgix implementation. Evaluated per-call rather than at module
// init so that the flag works even if `.env` isn't loaded by the time this
// module is first imported.
export const imgIxImageScaler: ImageScaler = (opts) =>
  getUseBunnyCdnUserFiles()
    ? userFilesBunnyImageScaler(opts)
    : imgIxImageScalerLegacy(opts);

// Rewrites a legacy `xwuf.imgix.net` URL to the Bunny pull zone, preserving
// the path and any existing query string. Use this for the few call sites
// that build image URLs by hand and bypass the scaler — typically because
// they append a transform like `?orient=N` and have no width/height to
// drive the scaler with.
export const rewriteLegacyUserFilesUrl = (src: string): string => {
  if (!getUseBunnyCdnUserFiles() || src.startsWith("data:")) {
    return src;
  }

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  if (url.host !== LEGACY_USERFILES_HOST) {
    return src;
  }

  const baseUrl = getBunnyCdnUserFilesBaseUrl().replace(/\/$/, "");
  return `${baseUrl}${url.pathname}${url.search}`;
};

// Apply logo rotation via Bunny's Image Optimizer `rotate` transform (90° increments). Rotation
// lives in the URL rather than CSS so it survives everywhere the served image is shown —
// including transactional emails, where mail clients strip CSS transforms. No-op for 0°/undefined
// to avoid needless transform params and CDN cache fragmentation. Composes with `&` when the URL
// already carries a query (e.g. a scaler's width/format params).
export const withBunnyRotation = (
  src: string,
  rotationDeg: number | undefined | null
): string => {
  if (!rotationDeg || src.startsWith("data:")) {
    return src;
  }
  const separator = src.includes("?") ? "&" : "?";
  return `${src}${separator}rotate=${rotationDeg}`;
};

export const centraImgIxImageScaler: ImageScaler = ({
  src,
  width,
  height,
  format,
  rawOpts,
}) => {
  if (src.includes(".centra") && imgIxToken && imgIxToken !== "placeholder") {
    const query = `?auto=compress%2Cformat&w=${width}&fit=clamp${height ? `&h=${height}` : ""
      }&q=${format == "lowres" ? 5 : 100}${rawOpts ? "&" + rawOpts : ""}`;
    const encodedSrc = encodeURIComponent(src);
    const signature = md5(`${imgIxToken}/${encodedSrc}${query}`);

    return `${imgIxBaseUrl}${encodedSrc}${query}&s=${signature}`;
  } else {
    return src;
  }
};

export const centraBunnyImageScaler: ImageScaler = ({
  src,
  width,
  height,
  format,
  rawOpts,
}) => {
  if (!src.includes(".centra")) {
    return src;
  }

  let pathname: string;
  try {
    pathname = new URL(src).pathname;
  } catch {
    return src;
  }

  const baseUrl = getBunnyCdnCentraBaseUrl().replace(/\/$/, "");
  const query =
    `?width=${width}` +
    (height ? `&height=${height}` : "") +
    `&format=auto` +
    `&quality=${format == "lowres" ? 5 : 100}` +
    (rawOpts ? `&${rawOpts}` : "");

  return `${baseUrl}${pathname}${query}`;
};

// Product images live on their own Bunny pull zone since the Centra shutdown migration
// (#225): catalog URLs already point at the zone, so unlike the Centra scaler there is no
// host rewrite — just append Bunny Optimizer transform params.
//
// Quality 80, not 100: a 706px WebP thumbnail is ~335 KB at 100 vs ~89 KB at 80 with no
// visible difference, and iPhones (DPR 3) pick that 706px candidate for every grid card.
const productImageQuality = 80;
export const productBunnyImageScaler: ImageScaler = ({
  src,
  width,
  height,
  format,
  rawOpts,
}) => {
  if (src.startsWith("data:")) {
    return src;
  }

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return src;
  }

  const baseUrl = getBunnyCdnProductsBaseUrl().replace(/\/$/, "");
  let productsHost: string;
  try {
    productsHost = new URL(baseUrl).host;
  } catch {
    return src;
  }

  if (url.host !== productsHost) {
    return src;
  }

  const query =
    `?width=${width}` +
    (height ? `&height=${height}` : "") +
    `&format=auto` +
    `&quality=${format == "lowres" ? 5 : productImageQuality}` +
    (rawOpts ? `&${rawOpts}` : "");

  return `${baseUrl}${url.pathname}${query}`;
};

export const storyblokImageScaler: ImageScaler = ({
  src,
  width,
  height,
  format,
}) => {
  if (src.includes("storyblok.com") || src.startsWith("/")) {
    if (src.startsWith("/")) {
      src = expandStoryblokImageUrl(src);
    }

    return src.startsWith("https://")
      ? `${src.replace(/ /g, "%20")}/m/${width}x${height ?? 0
      }/filters:quality(${format == "lowres" ? 5 : 75})`
      : src;
  } else {
    return src;
  }
};

/* Local image that can be used as fallback when images are missing */
export const placeholderImageSrc = "/static/placeholder.png";

export const defaultProductSizing = {
  aspect: productImageAspect,
};

export const defaultLogoSizing = {
  aspect: 1,
  rawOpts:
    // there is some sort of bug in imgix that breaks transparent images that are scaled with the
    // default parameters, so for logos - that are likely to be transparent - work around it with
    // these settings.
    "fit=fill&fill=solid&fill-color=0FFF",
};

export function registerImageScalers() {
  registerImageScaler(imgIxImageScaler);
  registerImageScaler(
    getUseBunnyCdnCentra() ? centraBunnyImageScaler : centraImgIxImageScaler
  );
  registerImageScaler(productBunnyImageScaler);
  registerImageScaler(storyblokImageScaler);
}
